/**
 * Authenticated workspace bootstrap.
 *
 * Order matters here:
 *  1. the transport learns how to react to a 401
 *  2. the session store learns how to drop cached tenant rows
 *  3. `GET /tenants` doubles as the session probe and as the tenant source
 *  4. the transport is told which tenant is active before any page query runs
 *  5. only then are the routes rendered
 *
 * Step 5 waits for step 3 because a page mounted before the tenant header exists
 * would fetch the wrong (or no) tenant scope. The gate shows an explicit message for
 * each outcome instead of rendering an empty workspace.
 */

import { useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchTenants, setUnauthorizedHandler } from '../api/client';
import { explainApiError, isApiError } from '../api/errors';
import { sessionStore, useSession } from './useSession';
import { queryKeys, sensitiveQueryKeyPrefixes } from './queryKeys';
import { TenantProvider } from './TenantContext';
import { AppShell } from './AppShell';
import { Notice } from '../components/ui';
import type { ApiError } from '../api/errors';
import type { Tenant } from '../api/types';

type ProbeOutcome = 'ok' | 'unauthorized' | 'unavailable';

function classifyProbeError(error: unknown): ProbeOutcome {
  if (isApiError(error) && error.kind === 'unauthorized') return 'unauthorized';
  return 'unavailable';
}

export function WorkspaceBootstrap(): JSX.Element {
  const session = useSession();
  const queryClient = useQueryClient();

  // A 401 from any endpoint means the session is gone; the store clears the token
  // and the guard below routes back to sign-in.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      sessionStore.handleUnauthorized();
    });
  }, []);

  // The store ends sessions (explicit sign-out, a 401, or a tenant switch) and the
  // query cache holds the rows; this is the one place the two are connected.
  useEffect(() => {
    const dropSensitiveCaches = (): void => {
      void queryClient.cancelQueries();
      for (const queryKey of sensitiveQueryKeyPrefixes()) {
        queryClient.removeQueries({ queryKey: [...queryKey] });
      }
    };
    sessionStore.setTenantDataInvalidationHook(dropSensitiveCaches);
    return () => {
      sessionStore.setTenantDataInvalidationHook(null);
    };
  }, [queryClient]);

  const tenantsQuery = useQuery({
    queryKey: queryKeys.tenants(),
    queryFn: ({ signal }) => fetchTenants(signal),
    // Retry once so a transient restart of the API does not immediately look like
    // an unauthenticated session.
    retry: 1,
  });

  const outcome: ProbeOutcome | null = tenantsQuery.isSuccess
    ? 'ok'
    : tenantsQuery.isError
      ? classifyProbeError(tenantsQuery.error)
      : null;

  useEffect(() => {
    if (outcome === null) return;
    if (outcome === 'ok') {
      sessionStore.resolveProbe('ok');
      return;
    }
    if (outcome === 'unauthorized') {
      sessionStore.resolveProbe('unauthorized');
      return;
    }
    const error: ApiError | null = isApiError(tenantsQuery.error) ? tenantsQuery.error : null;
    sessionStore.resolveProbe('unavailable', error ?? undefined);
  }, [outcome, tenantsQuery.error]);

  // A fresh array would change identity on every render, making the memo and effect
  // below re-run each time; an empty list is the stable default.
  const tenants: readonly Tenant[] = useMemo(
    () => tenantsQuery.data?.items ?? [],
    [tenantsQuery.data],
  );

  // Restore the previously selected tenant when it is still available to this user.
  const selectedTenantId = useMemo(() => {
    const remembered = session.tenantId;
    if (remembered !== null && tenants.some((tenant) => tenant.id === remembered)) {
      return remembered;
    }
    const first = tenants[0];
    return first === undefined ? null : first.id;
  }, [session.tenantId, tenants]);

  useEffect(() => {
    if (selectedTenantId === null) return;
    // Persist the effective selection so a refresh inside the same session restores
    // it. The store clears this on sign-out, so it can never carry over to the next
    // user of the same browser. The transport reads the value from the session store
    // on the next request, so no sync step is needed here.
    if (selectedTenantId !== session.tenantId) {
      sessionStore.setTenant(selectedTenantId, tenants);
    }
  }, [selectedTenantId, session.tenantId, tenants]);

  if (session.status !== 'authenticated') {
    // RequireSession normally handles this; kept as a defensive branch so the
    // bootstrap never renders business pages for an anonymous session.
    return (
      <GatePanel>
        <Notice tone="info" title="需要登录" detail="会话已失效，请重新登录后继续。" />
      </GatePanel>
    );
  }

  if (
    outcome === null ||
    (outcome === 'ok' && selectedTenantId !== null && session.tenantId === null)
  ) {
    return (
      <GatePanel>
        <Notice
          tone="info"
          title="正在加载租户信息…"
          detail="正在确认会话并读取可访问的租户列表。"
        />
      </GatePanel>
    );
  }

  if (outcome === 'unavailable') {
    const { title, detail } = explainApiError(tenantsQuery.error);
    return (
      <GatePanel>
        <Notice
          tone="error"
          title={`无法加载租户列表：${title}`}
          detail={
            <>
              <div>{detail}</div>
              <div style={{ marginTop: 4 }}>
                需要先读取你可访问的租户，才能确定各页面显示哪个租户的数据。
              </div>
            </>
          }
          actions={
            <>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => {
                  void tenantsQuery.refetch();
                }}
                disabled={tenantsQuery.isFetching}
              >
                {tenantsQuery.isFetching ? '重试中…' : '重试'}
              </button>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => {
                  sessionStore.signOut();
                }}
              >
                退出登录
              </button>
            </>
          }
        />
      </GatePanel>
    );
  }

  if (session.user?.must_change_password) return <Navigate to="/password" replace />;
  if (session.user?.is_platform_operator && tenants.length === 0)
    return <Navigate to="/platform" replace />;

  if (tenants.length === 0) {
    return (
      <GatePanel>
        <Notice
          tone="warn"
          title="当前账号没有可访问的租户"
          detail="登录成功，但租户列表为空。请联系管理员将账号加入至少一个租户后再使用工作台。"
        />
      </GatePanel>
    );
  }

  return (
    <TenantProvider tenants={tenants}>
      <AppShell probeWarning={null} />
    </TenantProvider>
  );
}

function GatePanel({ children }: { readonly children: JSX.Element }): JSX.Element {
  return (
    <div className="shell" style={{ gridTemplateColumns: '1fr', gridTemplateAreas: '"main"' }}>
      <main className="shell__main" style={{ paddingTop: 32 }}>
        <div style={{ maxWidth: 620, margin: '0 auto' }}>{children}</div>
      </main>
    </div>
  );
}
